/**
 * useOrganizationPaymentInfo — composable utilitaire qui dérive les
 * informations de paiement d'une organisation à partir d'un blob
 * "organisation" (au format `{ stripe_account_id, payment_providers,
 * fee_percentage, is_premium, logo, ... }`).
 *
 * Utilisé par `useDonationData` (QR/payment-link) et
 * `useCampaignDonationData` (cagnottes) pour éviter la duplication des
 * computeds `paymentProviders`, `hasActivePaymentProvider`,
 * `donoorFeeRate`, `donateUs`, `isPremiumPlan`, `organizationLogoUrl`.
 *
 * Pourquoi un composable séparé : on ne peut pas créer un composable
 * "parent" car les structures de fetch SSR sont différentes (URLs,
 * keys, gestion d'erreur). En revanche, les **dérivations** post-fetch
 * sont identiques — c'est ça qu'on factorise ici.
 */

import type { ComputedRef, Ref } from "vue";
import type { MoyenPaiementLocalPage } from "@donoor/checkout";

/** Forme normalisée des `payment_providers` exposée par le backend. */
export interface OrgPaymentProviders {
  stripe: {
    connected: boolean;
    charges_enabled: boolean;
    account_id: string | null;
    /**
     * Moyens de paiement LOCAUX du pays de l'association (Bancontact en
     * Belgique — chantier « autres pays », étape B). Absent pour une
     * association française.
     */
    moyens_locaux?: MoyenPaiementLocalPage[];
    /** « KvK 12345678 » : à afficher près du bouton iDEAL (règle iDEAL). */
    identifiant_officiel?: string;
    /** `false` quand l'association a éteint Link (02/11/2026) ; absent sinon. */
    link?: false;
  };
  paypal: {
    connected: boolean;
    charges_enabled: boolean;
    merchant_id: string | null;
    capabilities: Record<string, string> | null;
    /**
     * CardFields PayPal (formulaire carte intégré) utilisables — capability
     * PPCP_CUSTOM active, calculé serveur. Optionnel : API antérieure → le
     * front relit `capabilities` via isPaypalCardFieldsUsable.
     */
    card_fields_enabled?: boolean;
    /**
     * Tarif PayPal effectif appris (frais réels observés). Sert à estimer
     * « couvrir les frais » au bon taux en PayPal + libellé UI. Optionnels :
     * API/orgs anciennes n'exposent pas ces champs → défaut asso côté front.
     */
    fee_percentage?: number; // ex: 1.4 ou 2.9
    fee_fixed?: number; // ex: 0.25
    fee_tier?: "association" | "entreprise" | "micropaiement";
  };
  /**
   * Provider mis en avant par l'org (carte/Apple Pay/Google Pay du primaire).
   * Défaut "stripe" si absent (orgs/API anciennes). N'a d'effet que si les 2
   * providers sont utilisables.
   */
  preferred_provider?: "stripe" | "paypal";
  accepts_payments: boolean;
}

interface OrganizationLike {
  id?: string | number;
  name?: string | null;
  logo?: string | null;
  stripe_account_id?: string | null;
  fee_percentage?: number;
  is_premium?: boolean;
  payment_providers?: OrgPaymentProviders;
  // QR/payment_link expose payment_method_configuration_id, campaigns non
  payment_method_configuration_id?: string;
}

export interface UseOrganizationPaymentInfoReturn {
  paymentProviders: ComputedRef<OrgPaymentProviders>;
  hasActivePaymentProvider: ComputedRef<boolean>;
  stripeAccountId: ComputedRef<string | null>;
  paymentMethodConfigurationId: ComputedRef<string | null>;
  donoorFeeRate: ComputedRef<number>;
  donateUs: ComputedRef<boolean>;
  isPremiumPlan: ComputedRef<boolean>;
  organizationLogoUrl: ComputedRef<string>;
}

/**
 * Construit l'URL absolue d'un asset Directus (logo, image, fichier).
 *  - URL absolue → renvoyée telle quelle
 *  - Path local (/...) → renvoyé tel quel
 *  - UUID Directus → préfixé avec l'URL directus
 *  - Falsy → `fallback` (ou null si non fourni)
 *
 * Exporté pour être réutilisé par les composables qui exposent d'autres
 * assets (images de campagne, médias d'événements, etc.) sans dupliquer
 * la logique de résolution.
 */
export function buildDirectusAssetUrl(
  asset: string | null | undefined,
  assetsBaseUrl: string,
  fallback: string | null = null,
  width?: number,
): string | null {
  if (!asset) return fallback;
  if (asset.startsWith("http") || asset.startsWith("/")) return asset;
  // width → transformation Directus (resize + webp) : sans elle, l'image de
  // l'asso (logo ou photo de cagnotte 2-3 Mo) était servie brute sur la page
  // de paiement (LCP critique du tunnel de don). (Audit perf 2026-07-31.)
  const base = `${assetsBaseUrl}/api/assets/${asset}`;
  if (!width) return base;
  return `${base}?width=${width}&format=webp&quality=82&fit=cover`;
}

/** Fallback global pour le logo Donoor (utilisé quand l'asso n'a pas de logo). */
const DEFAULT_ORG_LOGO = "/newLogo/logo-mark-white.svg";

/**
 * Dérive les infos paiement d'une organisation à partir d'un ref/computed.
 *
 * @param org - Ref ou ComputedRef de l'objet organisation (peut être null
 *              pendant le chargement)
 * @returns Les computeds normalisés (toujours définis, valeurs par défaut
 *          si org est null)
 */
export function useOrganizationPaymentInfo(
  org: Ref<OrganizationLike | null | undefined>
    | ComputedRef<OrganizationLike | null | undefined>,
): UseOrganizationPaymentInfoReturn {
  const config = useRuntimeConfig();
  const assetsBaseUrl =
    (config.public.apiUrl as string) || "https://api.donoor.org";

  /**
   * Providers de paiement normalisés (Stripe + PayPal). Fallback sur la
   * déduction depuis `stripe_account_id` si l'org n'expose pas encore
   * `payment_providers` (rétrocompat anciens endpoints).
   */
  const paymentProviders = computed<OrgPaymentProviders>(() => {
    const o = org.value;
    if (o?.payment_providers && typeof o.payment_providers === "object") {
      return o.payment_providers;
    }
    const hasStripe = !!o?.stripe_account_id;
    return {
      stripe: {
        connected: hasStripe,
        charges_enabled: hasStripe,
        account_id: o?.stripe_account_id || null,
      },
      paypal: {
        connected: false,
        charges_enabled: false,
        merchant_id: null,
        capabilities: null,
      },
      accepts_payments: hasStripe,
    };
  });

  const hasActivePaymentProvider = computed(
    () => paymentProviders.value.accepts_payments,
  );

  const stripeAccountId = computed<string | null>(
    () => paymentProviders.value.stripe.account_id,
  );

  const paymentMethodConfigurationId = computed<string | null>(
    () => org.value?.payment_method_configuration_id || null,
  );

  /** Taux de commission Donoor en décimal (0.015 = 1.5%). Default 1.5%. */
  const donoorFeeRate = computed<number>(() => {
    const fee = org.value?.fee_percentage;
    if (typeof fee === "number") return fee / 100;
    return 0.015;
  });

  /**
   * Flag "Soutenir Donoor" affiché sur la page.
   *
   * ⚠️ RÈGLE PRODUIT (cf. memory rule_donate_us_always_visible) :
   *    Le module "Soutenir Donoor" doit être TOUJOURS visible sur TOUTES
   *    les pages de paiement, peu importe l'org. C'est le modèle freemium
   *    de Donoor : Donoor est 100% gratuit pour les assos, et on PROPOSE
   *    aux donateurs de contribuer pour que ça le reste.
   *
   * Toute logique conditionnelle (`is_premium`, `subscription_plan`, etc.)
   * est INTERDITE — elle masquerait notre seul levier de financement
   * volontaire côté donateur.
   */
  const donateUs = computed<boolean>(() => true);

  /** True si l'asso est premium (commission 0% via flag admin). */
  const isPremiumPlan = computed<boolean>(
    () => org.value?.is_premium === true,
  );

  const organizationLogoUrl = computed<string>(
    () =>
      buildDirectusAssetUrl(
        org.value?.logo,
        assetsBaseUrl,
        DEFAULT_ORG_LOGO,
        200,
      ) ?? DEFAULT_ORG_LOGO,
  );

  return {
    paymentProviders,
    hasActivePaymentProvider,
    stripeAccountId,
    paymentMethodConfigurationId,
    donoorFeeRate,
    donateUs,
    isPremiumPlan,
    organizationLogoUrl,
  };
}
